/**
 * ObstacleRenderer
 * Renders obstacles using InstancedMesh for efficient draw calls.
 * Two InstancedMesh instances: one for rectangles (BoxGeometry), one for circles (CylinderGeometry).
 * Supports per-instance color, opacity, and selection/collision highlighting.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { Obstacle } from '@/types/obstacle'
import { getObstacleVisibility } from '@/types/obstacle'

const MAX_INSTANCES = 200
const Z_FIGHTING_OFFSET = 0.005 // meters, lifted above floor

// Shared geometries (unit-sized, scaled per instance via matrix)
const BOX_GEOMETRY = new THREE.BoxGeometry(1, 1, 1)
const CYLINDER_GEOMETRY = new THREE.CylinderGeometry(1, 1, 1, 16)

// Shared materials
const OBSTACLE_MATERIAL = new THREE.MeshStandardMaterial({
  roughness: 0.7,
  metalness: 0.1,
  transparent: true,
})

const _position = new THREE.Vector3()
const _quaternion = new THREE.Quaternion()
const _scale = new THREE.Vector3()
const _matrix = new THREE.Matrix4()
const _color = new THREE.Color()
const _euler = new THREE.Euler()

export interface ObstacleRenderData {
  obstacles: Obstacle[]
  /** Unit offset from building origin in feet [x, y] */
  unitOffset: [number, number]
}

/**
 * Maps instance index to obstacle ID for raycasting/selection
 */
export interface ObstacleInstanceMap {
  rectangleMap: Map<number, string> // instanceIndex → obstacleId
  circleMap: Map<number, string>
}

export class ObstacleRenderer {
  private group: THREE.Group
  private rectMesh: THREE.InstancedMesh
  private circleMesh: THREE.InstancedMesh
  private instanceMap: ObstacleInstanceMap = {
    rectangleMap: new Map(),
    circleMap: new Map(),
  }

  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'obstacles'

    this.rectMesh = new THREE.InstancedMesh(
      BOX_GEOMETRY,
      OBSTACLE_MATERIAL.clone(),
      MAX_INSTANCES
    )
    this.rectMesh.name = 'obstacle-rectangles'
    this.rectMesh.count = 0
    this.rectMesh.frustumCulled = false

    this.circleMesh = new THREE.InstancedMesh(
      CYLINDER_GEOMETRY,
      OBSTACLE_MATERIAL.clone(),
      MAX_INSTANCES
    )
    this.circleMesh.name = 'obstacle-circles'
    this.circleMesh.count = 0
    this.circleMesh.frustumCulled = false

    this.group.add(this.rectMesh)
    this.group.add(this.circleMesh)
  }

  /** Get the Three.js group containing all obstacle meshes */
  getGroup(): THREE.Group {
    return this.group
  }

  /** Get instance maps for hit testing */
  getInstanceMap(): ObstacleInstanceMap {
    return this.instanceMap
  }

  /**
   * Update all obstacle instances.
   * Called when obstacles change (CRUD) or unit loads.
   */
  updateObstacles(renderData: ObstacleRenderData[]): void {
    this.instanceMap.rectangleMap.clear()
    this.instanceMap.circleMap.clear()

    let rectIndex = 0
    let circleIndex = 0

    for (const { obstacles, unitOffset } of renderData) {
      for (const obstacle of obstacles) {
        const visibility = getObstacleVisibility(obstacle)
        // Skip invisible obstacles that are manually deactivated AND not expired
        // (expired still show as ghosts)
        if (visibility === 'scheduled') continue

        const isGhost = visibility === 'expired'

        if (obstacle.shape === 'circle' && obstacle.radius != null) {
          this.setCircleInstance(
            circleIndex,
            obstacle,
            unitOffset,
            isGhost
          )
          this.instanceMap.circleMap.set(circleIndex, obstacle.id)
          circleIndex++
        } else if (
          obstacle.shape === 'rectangle' &&
          obstacle.width != null &&
          obstacle.depth != null
        ) {
          this.setRectInstance(
            rectIndex,
            obstacle,
            unitOffset,
            isGhost
          )
          this.instanceMap.rectangleMap.set(rectIndex, obstacle.id)
          rectIndex++
        }
      }
    }

    this.rectMesh.count = rectIndex
    this.circleMesh.count = circleIndex
    this.rectMesh.instanceMatrix.needsUpdate = true
    this.circleMesh.instanceMatrix.needsUpdate = true
    if (this.rectMesh.instanceColor) this.rectMesh.instanceColor.needsUpdate = true
    if (this.circleMesh.instanceColor) this.circleMesh.instanceColor.needsUpdate = true
  }

  /**
   * Set a rectangle obstacle instance
   */
  private setRectInstance(
    index: number,
    obstacle: Obstacle,
    unitOffset: [number, number],
    _isGhost: boolean
  ): void {
    const widthM = obstacle.width! * FEET_TO_METERS
    const depthM = obstacle.depth! * FEET_TO_METERS
    const heightM = obstacle.height * FEET_TO_METERS
    const elevationM = obstacle.elevation * FEET_TO_METERS

    // Position: unit-local feet → scene meters
    // DB: position_x → +X, position_y → -Z (north), height → +Y
    const posX = (unitOffset[0] + obstacle.positionX) * FEET_TO_METERS
    const posY = elevationM + heightM / 2 + Z_FIGHTING_OFFSET
    const posZ = -(unitOffset[1] + obstacle.positionY) * FEET_TO_METERS

    // Rotation around Y axis (degrees → radians)
    _euler.set(0, -(obstacle.rotation * Math.PI) / 180, 0)
    _quaternion.setFromEuler(_euler)

    _position.set(posX, posY, posZ)
    _scale.set(widthM, heightM, depthM)
    _matrix.compose(_position, _quaternion, _scale)

    this.rectMesh.setMatrixAt(index, _matrix)

    // Color
    _color.set(obstacle.color)
    this.rectMesh.setColorAt(index, _color)
  }

  /**
   * Set a circle obstacle instance
   */
  private setCircleInstance(
    index: number,
    obstacle: Obstacle,
    unitOffset: [number, number],
    _isGhost: boolean
  ): void {
    const radiusM = obstacle.radius! * FEET_TO_METERS
    const heightM = obstacle.height * FEET_TO_METERS
    const elevationM = obstacle.elevation * FEET_TO_METERS

    const posX = (unitOffset[0] + obstacle.positionX) * FEET_TO_METERS
    const posY = elevationM + heightM / 2 + Z_FIGHTING_OFFSET
    const posZ = -(unitOffset[1] + obstacle.positionY) * FEET_TO_METERS

    _position.set(posX, posY, posZ)
    _quaternion.identity()
    _scale.set(radiusM, heightM, radiusM)
    _matrix.compose(_position, _quaternion, _scale)

    this.circleMesh.setMatrixAt(index, _matrix)

    _color.set(obstacle.color)
    this.circleMesh.setColorAt(index, _color)
  }

  /**
   * Find obstacle ID from a raycast intersection
   */
  getObstacleIdFromIntersection(
    intersection: THREE.Intersection
  ): string | null {
    const instanceId = intersection.instanceId
    if (instanceId == null) return null

    if (intersection.object === this.rectMesh) {
      return this.instanceMap.rectangleMap.get(instanceId) ?? null
    }
    if (intersection.object === this.circleMesh) {
      return this.instanceMap.circleMap.get(instanceId) ?? null
    }
    return null
  }

  /**
   * Get raycastable meshes for hit testing
   */
  getRaycastTargets(): THREE.Object3D[] {
    return [this.rectMesh, this.circleMesh]
  }

  /**
   * Dispose all GPU resources
   */
  dispose(): void {
    this.rectMesh.dispose()
    this.circleMesh.dispose()
    ;(this.rectMesh.material as THREE.Material).dispose()
    ;(this.circleMesh.material as THREE.Material).dispose()
    this.group.clear()
  }
}
